import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

// import { ReactComponent as PdfIcon } from '../../assets/icons/export-pdf.svg';
// import { ReactComponent as ZipIcon } from '../../assets/icons/export-zip.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/export-download.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  DiagnosticFirmwareProperties,
  DiagnosticFirmwareRequest,
} from '../../interface/diagnostic-firmware.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  disableDiagnosticFirmwareFiltersApplied,
  fetchDiagnosticFirmware,
  fetchDiagnosticFirmwareFilters,
  refreshDiagnosticFirmware,
  setSortData,
  updateDiagnosticFirmwareFilters,
} from '../../redux/slices/DiagnosticFirmwareSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import CustomDateRangePicker from '../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../shared-components/custom-table/CustomTable';
// import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import FormatDuration from '../../shared-functions/FormatDuration';
import diagnosticFirmwareApi from '../../utils/api/diagnostic-firmware';

/**
 * Format file size in bytes to human-readable string.
 * @param {number | null | undefined} bytes - Size in bytes.
 * @returns {string} Formatted size string.
 */
const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) {
    return '-';
  }
  const numericBytes = Number(bytes);
  if (numericBytes < 1024) return `${numericBytes} B`;
  if (numericBytes < 1024 * 1024) return `${(numericBytes / 1024).toFixed(2)} KB`;
  return `${(numericBytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Extracts the original file name from a Content-Disposition header value,
 * e.g. `attachment; filename="diag-CP123.log"`.
 * @param {string | undefined} contentDisposition - The response header value.
 * @returns {string | null} The file name, or null if it couldn't be parsed.
 */
const getFileNameFromContentDisposition = (
  contentDisposition: string | undefined,
): string | null => {
  if (!contentDisposition) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (error) {
      // fall through to the other patterns below
    }
  }
  const quotedMatch = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quotedMatch?.[1]) return quotedMatch[1];
  const bareMatch = /filename=([^;]+)/i.exec(contentDisposition);
  return bareMatch?.[1]?.trim() || null;
};

/**
 * Diagnostic firmware component properties.
 */
export interface DiagnosticFirmwareComponentProperties {
  chargingPointSn?: string;
}

/**
 * Diagnostic firmware management and log view component.
 * @param {DiagnosticFirmwareComponentProperties} properties - The component properties.
 * @returns {React.ReactElement} The rendered DiagnosticFirmware component.
 */
function DiagnosticFirmware(
  properties: DiagnosticFirmwareComponentProperties = {},
): React.ReactElement {
  const { chargingPointSn: propertyChargingPointSn } = properties;
  const { t, i18n } = useTranslation(['devices', 'sidePanel', 'transaction', 'ocpi']);
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const { selectedChargingPoint } = useAppSelector((state) => state.device);
  const deviceId = id || selectedChargingPoint?.id || '';
  const chargerSerialNumber =
    propertyChargingPointSn || selectedChargingPoint?.chargingPointSn || '';

  const {
    diagnostics,
    limit,
    page,
    totalPages,
    totalResults,
    loading,


    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.diagnosticFirmware);

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = (): void => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.ReactElement} The rendered date filter elements.
   */
  const renderDateFilters = (): React.ReactElement => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start: string, end: string) =>
            setDateRange({ startDate: start, endDate: end })
          }
          defaultPreset="last14Days"
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
        />
      </div>
    );
  };

  /**
   * Fetch diagnostic firmware list data.
   * @param {DiagnosticFirmwareRequest} data - The request data payload.
   */
  const fetchData = (data: DiagnosticFirmwareRequest): void => {
    if (!chargerSerialNumber) {
      dispatch(
        setNotificationMessage({
          message: t(
            'chargingPointSnRequiredMessage',
            'A charging point serial number is required to load diagnostics.',
          ),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage', 'Date range should not exceed 31 days'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      const payload: DiagnosticFirmwareRequest = { ...data };
      if (deviceId) {
        payload.id = deviceId;
        payload.deviceId = deviceId;
      }
      payload.chargingPointSn = chargerSerialNumber;
      dispatch(fetchDiagnosticFirmware(payload));
    }
  };

  /**
   * Function to refresh data.
   */
  const handleRefresh = (): void => {

    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchData({
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      id: deviceId,
      deviceId,

      ...sortValue,
    });
  };

  /**
   * Function for handling auto refresh.
   */
  const handleAutoRefresh = (): void => {
    if (!chargerSerialNumber) return;


    const payload: DiagnosticFirmwareRequest = {
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      id: deviceId,
      deviceId,

      sortBy,
      sortField,
      chargingPointSn: chargerSerialNumber,
    };

    dispatch(refreshDiagnosticFirmware(payload));
  };

  /**
   * Column definitions for the table export.
   */
  const currentColumns = [
    { label: t('sNo', 'S.No'), accessorKey: '__serial__' },
    { label: t('chargingPoint', 'Charging Point'), accessorKey: 'chargingPointSn' },
    { label: t('fileUploadStatus', 'File Upload Status'), accessorKey: 'status' },
    { label: t('startTime', 'Start Time'), accessorKey: 'startTime' },
    { label: t('stopTime', 'Stop Time'), accessorKey: 'stopTime' },
    { label: t('timeout', 'Timeout'), accessorKey: 'timeout' },
    { label: t('retries', 'Retries'), accessorKey: 'retries' },
    { label: t('retryInterval', 'Retry Interval'), accessorKey: 'retryInterval' },
    { label: t('fileName', 'File Name'), accessorKey: 'fileName' },
    { label: t('fileSize', 'File Size'), accessorKey: 'fileSize' },
    { label: t('transferScheme', 'Transfer Scheme'), accessorKey: 'transferScheme' },
    { label: t('availableAt', 'Available At'), accessorKey: 'availableAt' },
    { label: t('sentAt', 'Sent At'), accessorKey: 'sentAt' },
    { label: t('created', 'Created'), accessorKey: 'createdAt' },
    { label: t('lastUpdated', 'Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns.
   */
  const exportCurrentColumns = currentColumns.filter(
    (exportColumn) =>
      exportColumn.accessorKey === '__serial__' ||
      !hiddenColumns.includes(exportColumn.accessorKey),
  );

  /**
   * Format a single diagnostic row for export.
   * @param {DiagnosticFirmwareProperties} rowData - The diagnostic row data.
   * @param {number} index - The row index.
   * @returns {any[]} Array of formatted cell values.
   */
  const formatExportRow = (rowData: DiagnosticFirmwareProperties, index: number): any[] => {
    return exportCurrentColumns.map((exportColumn) => {
      switch (exportColumn.accessorKey) {
        case '__serial__':
          return index + 1;
        case 'chargingPointSn':
          return rowData.chargingPointSn || t('na', 'N/A');
        case 'status':
          return rowData.status || '-';
        case 'startTime':
          return rowData.startTime ? ConvertUTCtoDateTime(rowData.startTime) : '-';
        case 'stopTime':
          return rowData.stopTime ? ConvertUTCtoDateTime(rowData.stopTime) : '-';
        case 'timeout': {
          const seconds = rowData.timeoutSeconds ?? rowData.timeout;
          return seconds !== undefined && seconds !== null && seconds !== ''
            ? FormatDuration(seconds, { formatBy: 'sec' })
            : '-';
        }
        case 'retries':
          return rowData.retries ?? '-';
        case 'retryInterval':
          return rowData.retryInterval ?? '-';
        case 'fileName':
          return rowData.storedFileName || rowData.reportedFileName || '-';
        case 'fileSize':
          return formatFileSize(rowData.sizeBytes);
        case 'transferScheme':
          return rowData.transferScheme || '-';
        case 'availableAt':
          return rowData.availableAt ? ConvertUTCtoDateTime(rowData.availableAt) : '-';
        case 'sentAt':
          return rowData.sentAt ? ConvertUTCtoDateTime(rowData.sentAt) : '-';
        case 'createdAt':
          return rowData.createdAt ? ConvertUTCtoDateTime(rowData.createdAt) : '-';
        case 'updatedAt':
          return rowData.updatedAt ? ConvertUTCtoDateTime(rowData.updatedAt) : '-';
        default:
          return (rowData as any)[exportColumn.accessorKey] || t('na', 'N/A');
      }
    });
  };

  /**
   * Download a single diagnostic log request file, keeping the original
   * file name reported by the server (from the Content-Disposition header)
   * instead of renaming it.
   * @param {DiagnosticFirmwareProperties} currentRow - The diagnostic log row data.
   */
  const downloadDiagnosticLogFile = async (
    currentRow: DiagnosticFirmwareProperties,
  ): Promise<void> => {
    if (!currentRow.id) return;
    setDownloadingRowId(currentRow.id);
    try {
      const response = await diagnosticFirmwareApi.downloadDiagnosticLog(currentRow.id);
      const fileName =
        getFileNameFromContentDisposition(response.headers?.['content-disposition']) ||
        currentRow.reportedFileName ||
        currentRow.storedFileName ||
        `diagnostic-${currentRow.chargingPointSn || currentRow.id || 'log'}.log`;

      const url = window.URL.createObjectURL(response.data);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(
        setNotificationMessage({
          message: t('downloadFailed', 'Failed to download the diagnostic log.'),
          status: true,
          type: 'error',
          code: 500,
        }),
      );
    } finally {
      setDownloadingRowId(null);
    }
  };

  const column = [
    {
      label: t('chargingPointSn', 'ChargingPoint Sn'),
      accessorKey: 'chargingPointSn' as keyof DiagnosticFirmwareProperties,
      sortAllow: false,
      /**
       * Charging point custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Charging point serial element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{row.chargingPointSn || '-'}</span>
      ),
    },
    {
      label: t('fileUploadStatus', 'File Upload Status'),
      accessorKey: 'status' as keyof DiagnosticFirmwareProperties,
      sortAllow: true,
      /**
       * Status custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Status element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => {
        const statusClass = (row.status || '').toLowerCase().replace(/\s+/g, '-');
        return <span className={`status-badge ${statusClass}`}>{row.status || '-'}</span>;
      },
    },
    {
      label: t('startTime', 'Start Time'),
      accessorKey: 'startTime' as keyof DiagnosticFirmwareProperties,
      sortAllow: true,
      /**
       * Start time custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Start time element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{row.startTime ? ConvertUTCtoDateTime(row.startTime) : '-'}</span>
      ),
    },
    {
      label: t('stopTime', 'Stop Time'),
      accessorKey: 'stopTime' as keyof DiagnosticFirmwareProperties,
      sortAllow: true,
      /**
       * Stop time custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Stop time element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{row.stopTime ? ConvertUTCtoDateTime(row.stopTime) : '-'}</span>
      ),
    },
    {
      label: t('timeout', 'Timeout'),
      accessorKey: 'timeoutSeconds' as keyof DiagnosticFirmwareProperties,
      /**
       * Timeout custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Timeout element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => {
        const seconds = row.timeoutSeconds ?? row.timeout;
        return (
          <span>
            {seconds !== undefined && seconds !== null && seconds !== ''
              ? FormatDuration(seconds, { formatBy: 'sec' })
              : '-'}
          </span>
        );
      },
    },
    {
      label: t('retries', 'Retries'),
      accessorKey: 'retries' as keyof DiagnosticFirmwareProperties,
      /**
       * Retries custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Retries element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{row.retries !== undefined && row.retries !== null ? row.retries : '-'}</span>
      ),
    },
    {
      label: t('retryInterval', 'Retry Interval'),
      accessorKey: 'retryInterval' as keyof DiagnosticFirmwareProperties,
      /**
       * Retry interval custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Retry interval element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>
          {row.retryInterval !== undefined && row.retryInterval !== null && row.retryInterval !== ''
            ? `${row.retryInterval}s`
            : '-'}
        </span>
      ),
    },
    {
      label: t('fileName', 'File Name'),
      accessorKey: 'storedFileName' as keyof DiagnosticFirmwareProperties,
      /**
       * File name custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} File name element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{row.storedFileName || row.reportedFileName || '-'}</span>
      ),
    },
    {
      label: t('fileSize', 'File Size'),
      accessorKey: 'sizeBytes' as keyof DiagnosticFirmwareProperties,
      /**
       * File size custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} File size element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{formatFileSize(row.sizeBytes)}</span>
      ),
    },
    {
      label: t('transferScheme', 'Transfer Scheme'),
      accessorKey: 'transferScheme' as keyof DiagnosticFirmwareProperties,
      /**
       * Transfer scheme custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Transfer scheme element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{row.transferScheme || '-'}</span>
      ),
    },
    {
      label: t('availableAt', 'Available At'),
      accessorKey: 'availableAt' as keyof DiagnosticFirmwareProperties,
      /**
       * Available at custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Available at element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{row.availableAt ? ConvertUTCtoDateTime(row.availableAt) : '-'}</span>
      ),
    },
    {
      label: t('sentAt', 'Sent At'),
      accessorKey: 'sentAt' as keyof DiagnosticFirmwareProperties,
      /**
       * Sent at custom formatter.
       * @param {DiagnosticFirmwareProperties} row - Row data.
       * @returns {React.ReactElement} Sent at element.
       */
      customData: (row: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{row.sentAt ? ConvertUTCtoDateTime(row.sentAt) : '-'}</span>
      ),
    },
    {
      label: t('created', 'Created'),
      accessorKey: 'createdAt' as keyof DiagnosticFirmwareProperties,
      /**
       * Render creation date and time.
       * @param {DiagnosticFirmwareProperties} rowData - The row data.
       * @returns {React.ReactElement} Formatted date time element.
       */
      customData: (rowData: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{rowData.createdAt ? ConvertUTCtoDateTime(rowData.createdAt) : '-'}</span>
      ),
    },
    {
      label: t('lastUpdated', 'Last Updated'),
      accessorKey: 'updatedAt' as keyof DiagnosticFirmwareProperties,
      /**
       * Render updated date and time.
       * @param {DiagnosticFirmwareProperties} rowData - The row data.
       * @returns {React.ReactElement} Formatted date time element.
       */
      customData: (rowData: DiagnosticFirmwareProperties): React.ReactElement => (
        <span>{rowData.updatedAt ? ConvertUTCtoDateTime(rowData.updatedAt) : '-'}</span>
      ),
    },
    {
      label: t('action', 'Action'),
      accessorKey: 'action' as keyof DiagnosticFirmwareProperties,
      sortAllow: false,
      columnWidth: 80,
      hideWhenSidePanelOpen: true,
      sticky: 'right' as const,
      /**
       * Action custom formatter.
       * @param {DiagnosticFirmwareProperties} currentRow - Row data.
       * @returns {React.ReactElement} Action element with a download icon/spinner.
       */
      customData: (currentRow: DiagnosticFirmwareProperties): React.ReactElement => {
        const isDownloading = downloadingRowId === currentRow.id;
        const isAvailable = currentRow.status === 'Available';
        return (
          <div className="table-action-icons">
            <button
              type="button"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                downloadDiagnosticLogFile(currentRow);
              }}
              disabled={isDownloading || !isAvailable}
              className={`table-icon-button download-icon-wrapper ${
                !isAvailable ? 'unavailable' : ''
              }`}
              title={
                isAvailable
                  ? t('download', 'Download')
                  : t(
                      'downloadUnavailable',
                      'Download is only available once the diagnostics log has finished uploading (status: Available).',
                    )
              }>
              {isDownloading ? <span className="download-spinner" /> : <DownloadIcon />}
            </button>
          </div>
        );
      },
    },
  ];

  /**
   * Table pagination limit change function.
   * @param {any} value - The pagination parameter object.
   */
  const pageNateLimitChange = (value: any): void => {

    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchData({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      id: deviceId,
      deviceId,

      ...sortValue,
    });
  };

  /**
   * Export data function for pdf and csv.
   * @param {string} type - Export format ('csv' | 'pdf').
   */
  const exportDataPdfOrCSV = (type: string): void => {
    if (!chargerSerialNumber) {
      dispatch(
        setNotificationMessage({
          message: t(
            'chargingPointSnRequiredMessage',
            'A charging point serial number is required to load diagnostics.',
          ),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    setExportLoader(true);

    const payload: Record<string, any> = {

      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      id: deviceId,
      deviceId,
      chargingPointSn: chargerSerialNumber,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    diagnosticFirmwareApi
      .fetchDiagnosticFirmwareExport(payload)
      .then((response: any) => {
        const headers = exportCurrentColumns.map((exportColumn) => exportColumn.label);
        const dataItems = Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || (response?.data ? [response.data] : []);

        const rows = dataItems.map((row: DiagnosticFirmwareProperties, index: number) =>
          formatExportRow(row, index),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'diagnostic-firmware.csv');
          } else {
            exportPDF(headers, rows, 'diagnostic-firmware.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message:
              error?.response?.data?.message || t('somethingWentWrong', 'Something went wrong!'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the sort data function.
   * @param {any} sortData - The sorting configuration.
   */
  const handleSortFunction = (sortData: any): void => {

    dispatch(setSortData(sortData));
    fetchData({

      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      id: deviceId,
      deviceId,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (
      isFilterUpdated ||
      diagnostics.length === 0 ||
      dateRange.startDate ||
      dateRange.endDate ||
      chargerSerialNumber ||
      deviceId
    ) {
  
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        id: deviceId,
        deviceId,
  
        sortBy,
        sortField,
      });
      dispatch(disableDiagnosticFirmwareFiltersApplied());
    }
  }, [isFilterUpdated, dateRange.endDate, dateRange.startDate, chargerSerialNumber, deviceId]);

  useEffect(() => {
    dispatch(
      fetchDiagnosticFirmwareFilters(
        chargerSerialNumber ? { chargingPointSn: chargerSerialNumber } : undefined,
      ),
    );
  }, [chargerSerialNumber]);

  useEffect(() => {
    if (
      searchParameters.get('deviceId') ||
      searchParameters.get('userCustomId') ||
      searchParameters.get('transactionObjectId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      parameters.delete('userCustomId');
      parameters.delete('transactionObjectId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="diagnostic-firmware-wrap" dir={i18n.dir()}>
      <div className="diagnostic-firmware-right-content">
        <CustomTable
          dataKey="DiagnosticFirmware"
          version="v2"
          header={column}
          data={diagnostics}
          actionButton
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired

          filtersToDispatch={updateDiagnosticFirmwareFilters}

          filterListToDispatch={fetchDiagnosticFirmwareFilters}
          requiredRefreshToggle
          refreshRequired
          refreshFunction={handleAutoRefresh}
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          hoverLabel={t('refresh', 'Refresh')}
          rightTopContent={renderDateFilters()}
          onClearFilterChange={clearDateRange}
          height={340}
        />
      </div>
    </div>
  );
}

DiagnosticFirmware.defaultProps = {
  chargingPointSn: '',
};

export default DiagnosticFirmware;
